import { useMemo } from "react";

import { KeyBy } from "@m7-health/shared-utils";
import { at, compact, isEqual } from "lodash";
import { createSelector } from "reselect";

import { TRootState } from "~/store";

import { IHealthSystem, IStaffJob, IStaffType, IUnit } from "@/api";
import { useAppFlags, useAppSelector } from "@/common/hooks";

export const useStaffJobs = () => {
  return useAppSelector((state) => state.appConfig.staffJob.staffJobs);
};

export function useStaffJobById(): KeyBy<IStaffJob, "id">;
export function useStaffJobById(id: IStaffJob["id"] | undefined): IStaffJob | undefined;
export function useStaffJobById(
  id?: IStaffJob["id"],
): KeyBy<IStaffJob, "id"> | IStaffJob | undefined {
  const staffJobById = useAppSelector((state) => state.appConfig.staffJob.staffJobById);

  if (arguments.length === 0) return staffJobById;

  // If a value is explicitly provided, even if it's undefined, return one value
  if (id) return staffJobById[id];
  return undefined;
}

const unitStaffJobSelector = (unitId?: IUnit["id"]) =>
  createSelector(
    (state: TRootState) => state.appConfig.unit.unitById[unitId ?? ""],
    (state: TRootState) => state.appConfig.staffJob.staffJobById,
    (foundUnit, staffJobById) => {
      if (!foundUnit) return [];

      return compact(at(staffJobById, foundUnit?.eligibleStaffJobIds));
    },
  );
export const useUnitStaffJobs = (unitId?: IUnit["id"]) => {
  return useAppSelector(unitStaffJobSelector(unitId), isEqual);
};

const healthSystemStaffJobSelector = (healthSystemId?: IHealthSystem["id"]) =>
  createSelector(
    (state: TRootState) => state.appConfig.staffJob.staffJobs,
    (staffJobs) => {
      if (!healthSystemId) return [];

      return staffJobs.filter((staffJob) => staffJob.healthSystemId === healthSystemId);
    },
  );
export const useHealthSystemStaffJobs = (healthSystemId?: IHealthSystem["id"]) => {
  return useAppSelector(healthSystemStaffJobSelector(healthSystemId), isEqual);
};

export function useStaffTypeByStaffJobId(
  staffJobId?: IStaffJob["id"],
): Record<IStaffJob["id"], IStaffType> | undefined;
export function useStaffTypeByStaffJobId(staffJobId: IStaffJob["id"]): IStaffType | undefined;
export function useStaffTypeByStaffJobId(
  staffJobId?: IStaffJob["id"],
): IStaffType | undefined | Record<IStaffJob["id"], IStaffType> {
  const { staffJobOnShift } = useAppFlags();
  const { staffJobs, staffTypes } = useAppSelector((state) => ({
    staffJobs: state.appConfig.staffJob.staffJobs,
    staffTypes: state.appConfig.staffType.staffTypeByKey,
  }));

  const staffTypeByStaffJobId = useMemo(() => {
    if (!staffJobOnShift) return undefined;

    return staffJobs.reduce(
      (acc, staffJob) => {
        const staffType = staffTypes[staffJob.staffTypeKey];
        if (staffType) acc[staffJob.id] = staffType;

        return acc;
      },
      {} as Record<IStaffJob["id"], IStaffType>,
    );
  }, [staffJobOnShift, staffJobs, staffTypes]);

  if (arguments.length === 0) return staffTypeByStaffJobId;
  if (!staffJobId) return undefined;

  return staffTypeByStaffJobId?.[staffJobId];
}
